import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import CheckList from "../CheckList";
import SubSiteList from "../SubSiteList";
import { generateExcelForUserResponse } from "../../utils/excel";
import moment from "moment";
import SiteUserResponseList from "../SiteResponseList";
import EntityInfoV2View from "../common/EntityInfoV2View";
import { countNegativeResponses, countPositiveResponses } from "../../utils/util";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
const SiteInfoV2 = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [machineloading, setMachineLoading] = useState(true);
  const [tabActiveKey, setTabActiveKey] = useState("checklist");
  const [downloadAllNegativeResponsesZip, setDownloadAllNegativeResponsesZip] = useState(() => () => {});
  const [machineDetails, setMachineDetails] = useState({});
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [responseLength, setResponseLenght] = useState(0);
  const [questionLength, setQestionLength] = useState(0);
  const [visibleGroups, setVisibleGroups] = useState([]);
  const [hiddenGroups, setHiddenGroups] = useState([]);
  const [userResponse, setUserResponse] = useState([]);
  const [questionMapping, setQuestionMapping] = useState({});
  const [tabActions, setTabActions] = useState(null);
  const [responseTabActions, setResponseTabActions] = useState(null);
  const [siteListTabActions, setSiteListTabActions] = useState(null);
  const [positiveResponseCount, setPositiveResponseCount] = useState(0);
  const [negativeResponseCount, setNegativeResponseCount] = useState(0);
  const [subSiteCount, setSubSiteCount] = useState(0);

  const updateResponseStats = useCallback((responses = []) => {
    setPositiveResponseCount(countPositiveResponses(responses));
    setNegativeResponseCount(countNegativeResponses(responses));
  }, []);

  const fetchOverviewStats = useCallback(async () => {
    if (!uuid) return;
    try {
      const headers = {
        Authorization: `Bearer ${userData.token}`,
        "Content-Type": "application/json",
      };
      const [questionsRes, responsesRes, subsitesRes] = await Promise.all([
        axios.get(`${configData.config.API_URL}/api/machine/questions/all/${uuid}`, { headers }),
        axios.post(`${configData.config.API_URL}/api/site/responses/list/${uuid}`, {}, { headers }),
        axios.get(`${configData.config.API_URL}/api/site/subsites/list/${uuid}`, { headers }),
      ]);
      const questions = questionsRes.data?.data || [];
      const responses = responsesRes.data?.data?.list || [];
      setQestionLength(questions.length);
      setResponseLenght(responsesRes.data?.data?.totalCount || responses.length);
      updateResponseStats(responses);
      setSubSiteCount(subsitesRes.data?.data?.length || 0);
    } catch (error) {
      console.log("error", error);
    }
  }, [configData.config.API_URL, updateResponseStats, userData.token, uuid]);

  const fetchMachineDetails = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/machine/details/${uuid}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setMachineDetails(data.data);
      getGroupDetails(data.data.groups);
      setMachineLoading(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getGroupDetails = async (groups) => {
    try {
      const {
        data: { data: groupData },
      } = await axios.post(
        `${configData.config.UNO_URL}/api/user/bulk/group/details`,
        { groupIds: groups },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setVisibleGroups(groupData.slice(0, 2));
      if (groupData.length > 2) setHiddenGroups(groupData.slice(2));
    } catch (error) {
      console.log("error", error);
    }
  };

  const generateUserListExcel = () => {
    const staticHeaders = ["S.No.", "User", "Date & Time", "Health Status", "Designation", "Site Name", "Remarks"];
    const questionAndAttachmentHeaders = [...staticHeaders];
    Object.entries(questionMapping || {}).forEach(([, value]) => {
      questionAndAttachmentHeaders.push(value);
      questionAndAttachmentHeaders.push(`${value} Attachment`);
    });

    const data = userResponse?.map((item, idx) => {
      const comment = item?.data?.[0]?.comment || "";
      const sheetData = {
        "S.No.": idx + 1,
        User: item?.name || "",
        "Date & Time": `${moment(item?.createdAt).format("DD-MM-YYYY")} | ${moment(item?.createdAt).format("HH:mm")}`,
        "Health Status": item?.health || "",
        Designation: item?.designation || "",
        "Site Name": item?.site?.name || "",
        Remarks: comment.includes("@") ? comment : "",
      };

      Object.entries(questionMapping || {}).forEach(([key, value]) => {
        sheetData[value] = "";
        sheetData[`${value} Attachment`] = "";
      });

      item.data?.forEach((res) => {
        if (questionMapping[res.questionId]) {
          let answerValue;
          if (res.answer === "N/A") answerValue = "N/A";
          else if (res.answer?.toLowerCase() === "yes") answerValue = 1;
          else if (res.answer?.toLowerCase() === "no") answerValue = 0;
          else answerValue = res.answer || "";

          sheetData[questionMapping[res.questionId]] = answerValue;
          sheetData[`${questionMapping[res.questionId]} Attachment`] = `Attachment : ${
            res.attachments?.[0] || "No Attachment"
          } || Comment : ${res.comment || "No Comment"}`;
        }
      });

      return sheetData;
    });

    generateExcelForUserResponse(
      "User_List_Response",
      data,
      (questionAndAttachmentHeaders.length - staticHeaders.length) / 2,
      questionAndAttachmentHeaders,
      questionAndAttachmentHeaders,
      staticHeaders.length,
      machineDetails?.name || "Site",
    );
  };

  useEffect(() => {
    fetchMachineDetails();
    fetchOverviewStats();
  }, [tabActiveKey, fetchOverviewStats]);

  useEffect(() => {
    if (tabActiveKey !== "checklist") setTabActions(null);
  }, [tabActiveKey]);

  const handleDownloadNegativePdfs = async () => {
    try {
      setDownloadingZip(true);
      await downloadAllNegativeResponsesZip();
    } finally {
      setDownloadingZip(false);
    }
  };

  const currentTabActions =
    tabActiveKey === "checklist"
      ? tabActions
      : tabActiveKey === "response"
        ? responseTabActions
        : tabActiveKey === "site-list"
          ? siteListTabActions
          : null;

  const kpiCards = useMemo(
    () => [
      {
        key: "questions",
        icon: QuizOutlinedIcon,
        tone: "red",
        value: questionLength,
        label: "Total Questions",
        subtitle: "Total questions in this site",
      },
      {
        key: "responses",
        icon: GroupsOutlinedIcon,
        tone: "green",
        value: responseLength,
        label: "User response received",
        subtitle: "Total responses submitted",
      },
      {
        key: "positive",
        icon: CheckCircleOutlineIcon,
        tone: "blue",
        value: positiveResponseCount,
        label: "Total Positive Response",
        subtitle: "Responses with 100% health status",
      },
      {
        key: "negative",
        icon: CancelOutlinedIcon,
        tone: "orange",
        value: negativeResponseCount,
        label: "Total Negative Response",
        subtitle: "Responses below 100% health status",
      },
      {
        key: "sites",
        icon: ApartmentOutlinedIcon,
        tone: "purple",
        value: subSiteCount,
        label: "Total Sites",
        subtitle: "Sub-sites under this site",
      },
    ],
    [negativeResponseCount, positiveResponseCount, questionLength, responseLength, subSiteCount],
  );

  const tabs = useMemo(() => {
    const items = [];
    if (userData?.user?.permissions?.includes("MACHINE.QUESTIONS")) {
      items.push({ key: "checklist", label: "Questionnaire" });
    }
    if (userData?.user?.permissions?.includes("MACHINE.RESPONSES")) {
      items.push({ key: "response", label: "User Response" });
      items.push({ key: "site-list", label: "Site List" });
    }
    return items;
  }, [userData?.user?.permissions]);

  return (
    <EntityInfoV2View
      navigate={navigate}
      loading={machineloading}
      entityName={machineDetails.name}
      editPath={`/site-list/${uuid}`}
      thumbnail={machineDetails.thumbnail}
      responseCount={responseLength}
      questionCount={questionLength}
      serialNo={machineDetails?.data?.serialNo}
      entityIdLabel="Site ID"
      entityId={machineDetails?.data?.machineId}
      visibleGroups={visibleGroups}
      hiddenGroups={hiddenGroups}
      tags={machineDetails?.tags}
      kpiCards={kpiCards}
      tabs={tabs}
      activeTab={tabActiveKey}
      onTabChange={setTabActiveKey}
      tabActions={currentTabActions}
    >
      {tabActiveKey === "checklist" && userData?.user?.permissions?.includes("MACHINE.QUESTIONS.VIEW") && (
        <CheckList
          setQestionLength={setQestionLength}
          setQuestionMapping={setQuestionMapping}
          setTabActions={setTabActions}
        />
      )}
      {tabActiveKey === "response" && userData?.user?.permissions?.includes("MACHINE.RESPONSES") && (
        <SiteUserResponseList
          variant="v2"
          setResponseLenght={setResponseLenght}
          setUserResponse={setUserResponse}
          setTabActions={setResponseTabActions}
          onDownloadExcel={generateUserListExcel}
          onDownloadPdf={handleDownloadNegativePdfs}
          downloadingZip={downloadingZip}
          onResponsesChanged={fetchOverviewStats}
          setDownloadAllNegativeResponsesZip={setDownloadAllNegativeResponsesZip}
        />
      )}
      {tabActiveKey === "site-list" && userData?.user?.permissions?.includes("MACHINE.RESPONSES") && (
        <SubSiteList variant="v2" siteId={machineDetails?.uuid} setTabActions={setSiteListTabActions} />
      )}
    </EntityInfoV2View>
  );
};

export default SiteInfoV2;
